import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Scene } from "@babylonjs/core/scene";
import { WebXRDefaultExperience } from "@babylonjs/core/XR/webXRDefaultExperience";
import type { WebXRInputSource } from "@babylonjs/core/XR/webXRInputSource";
import { WebXRSessionManager } from "@babylonjs/core/XR/webXRSessionManager";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes";
import type { WebXRAbstractMotionController } from "@babylonjs/core/XR/motionController/webXRAbstractMotionController";
import type { WebXRControllerComponent } from "@babylonjs/core/XR/motionController/webXRControllerComponent";
import { DualStickArbiter, type StickSide, type StickVector } from "./dual-stick";
import { mapQuestControl, type QuestAction } from "./quest-control-mapping";

export type XRExitReason = "stage_feedback" | "scene_failed" | "pause" | "summary" | "user";

export interface QuestXRActions {
  readonly onPreviewOrCancel: () => void;
  readonly onPrimary: () => void;
  readonly onContinue: () => void;
  readonly onCancel: () => void;
  readonly onPause: () => void;
  readonly onMovementChanged: (moving: boolean, source: StickSide | null) => void;
  readonly onMoveRequested: (displacement: Vector3) => void;
  readonly onSessionStarted: () => void;
  readonly onSessionEnded: (reason: XRExitReason) => void;
}

const MOVEMENT_SPEED_METERS_PER_SECOND = 1.2;

export class QuestXRInput {
  private experience: WebXRDefaultExperience | null = null;
  private readonly arbiter = new DualStickArbiter();
  private readonly boundControllers = new WeakSet<WebXRAbstractMotionController>();
  private enabled = false;
  private wasMoving = false;
  private pendingExitReason: XRExitReason | null = null;

  constructor(private readonly scene: Scene, private readonly actions: QuestXRActions) {}

  async initialize(): Promise<boolean> {
    if (!await WebXRSessionManager.IsSessionSupportedAsync("immersive-vr")) return false;
    this.experience = await WebXRDefaultExperience.CreateAsync(this.scene, {
      disableDefaultUI: true,
      disablePointerSelection: true,
      disableTeleportation: true,
      disableNearInteraction: true,
      disableHandTracking: true,
      inputOptions: {
        doNotLoadControllerMeshes: true,
      },
      uiOptions: { sessionMode: "immersive-vr", referenceSpaceType: "local-floor" },
    });
    this.experience.input.onControllerAddedObservable.add((controller) => this.bindController(controller));
    this.experience.input.controllers.forEach((controller) => this.bindController(controller));
    this.experience.baseExperience.sessionManager.onXRSessionInit.add(() => {
      this.pendingExitReason = null;
      document.body.classList.add("xr-presenting");
      this.actions.onSessionStarted();
    });
    this.experience.baseExperience.sessionManager.onXRSessionEnded.add(() => {
      const reason = this.pendingExitReason ?? "user";
      this.pendingExitReason = null;
      document.body.classList.remove("xr-presenting");
      this.clearMovement();
      this.actions.onSessionEnded(reason);
    });
    return true;
  }

  get isSupported(): boolean {
    return this.experience !== null;
  }

  get isInXR(): boolean {
    return this.experience?.baseExperience.state === WebXRState.IN_XR;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.clearMovement();
  }

  async enter(): Promise<void> {
    if (!this.experience) throw new Error("此浏览器不支持 immersive-vr WebXR");
    if (this.isInXR) return;
    await this.experience.baseExperience.enterXRAsync("immersive-vr", "local-floor", this.experience.renderTarget, {
      optionalFeatures: ["local-floor", "bounded-floor"],
    });
  }

  async exit(reason: XRExitReason): Promise<void> {
    if (!this.experience || !this.isInXR) return;
    this.pendingExitReason = reason;
    await this.experience.baseExperience.exitXRAsync();
  }

  update(deltaSeconds: number): void {
    if (!this.enabled || !this.isInXR || !this.experience) return;
    const now = performance.now();
    this.arbiter.update("left", this.readStick("left"), now);
    this.arbiter.update("right", this.readStick("right"), now);
    const active = this.arbiter.getActive();
    const moving = active.side !== null;
    if (moving !== this.wasMoving) {
      this.wasMoving = moving;
      this.actions.onMovementChanged(moving, active.side);
    }
    if (!moving) return;

    const camera = this.experience.baseExperience.camera;
    const forward = camera.getDirection(Vector3.Forward());
    forward.y = 0;
    if (forward.lengthSquared() < 0.001) return;
    forward.normalize();
    const right = Vector3.Cross(Vector3.Up(), forward).normalize();
    const displacement = forward
      .scale(active.vector.y * MOVEMENT_SPEED_METERS_PER_SECOND * deltaSeconds)
      .add(right.scale(active.vector.x * MOVEMENT_SPEED_METERS_PER_SECOND * deltaSeconds));
    this.actions.onMoveRequested(displacement);
  }

  pulse(strength: number, durationMs: number): void {
    if (!this.experience) return;
    for (const controller of this.experience.input.controllers) {
      if (controller.motionController) void controller.motionController.pulse(strength, durationMs);
    }
  }

  private bindController(controller: WebXRInputSource): void {
    if (controller.motionController) this.bindMotionController(controller.motionController);
    controller.onMotionControllerInitObservable.add((motionController) => this.bindMotionController(motionController));
  }

  private bindMotionController(controller: WebXRAbstractMotionController): void {
    if (this.boundControllers.has(controller)) return;
    this.boundControllers.add(controller);
    const main = controller.getMainComponent();
    for (const componentId of controller.getComponentIds()) {
      const component = controller.getComponent(componentId);
      const action = mapQuestControl({
        handedness: controller.handedness,
        componentId,
        componentType: component.type,
        isMainComponent: component === main,
      });
      if (action) this.bindAction(component, action);
    }
  }

  private bindAction(component: WebXRControllerComponent, action: QuestAction): void {
    component.onButtonStateChangedObservable.add(() => {
      if (!this.enabled || !this.isInXR || component.changes.pressed?.current !== true) return;
      if (action === "previewOrCancel") this.actions.onPreviewOrCancel();
      else if (action === "primary") this.actions.onPrimary();
      else if (action === "continue") this.actions.onContinue();
      else this.actions.onPause();
    });
  }

  private readStick(side: StickSide): StickVector {
    if (!this.experience) return { x: 0, y: 0 };
    const source = this.experience.input.controllers.find((controller) => controller.inputSource.handedness === side);
    const component = source?.motionController?.getComponentOfType("thumbstick");
    if (component) return { x: component.axes.x, y: -component.axes.y };
    const axes = source?.motionController?.gamepadObject.axes ?? [];
    return { x: axes.at(-2) ?? 0, y: -(axes.at(-1) ?? 0) };
  }

  private clearMovement(): void {
    const now = performance.now();
    this.arbiter.update("left", { x: 0, y: 0 }, now);
    this.arbiter.update("right", { x: 0, y: 0 }, now);
    if (this.wasMoving) this.actions.onMovementChanged(false, null);
    this.wasMoving = false;
  }
}
